/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Fragment from 'ember-data-model-fragments/fragment';
import { computed } from '@ember/object';
import { filterBy } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { attr } from '@warp-drive/legacy/model';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import type { StatefulArray } from 'ember-data-model-fragments/array/stateful';
import { array, fragmentArray } from 'ember-data-model-fragments/attributes';
import type SessionService from '@linc-technologies/linced-common/services/session';
import {
  byKeys,
  filterBy as filterByArray,
  findBy,
} from '@linc-technologies/linced-common/utils/arrays';
import ContactType from 'linced-app/enums/contact-type';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import type Contact from 'linced-app/models/people/contact';
import type EnrolmentStub from 'linced-app/models/people/enrolment-stub';
import type MedicalCondition from 'linced-app/models/people/medical-condition';
import type MedicalContact from 'linced-app/models/people/medical-contact';
import type Sibling from 'linced-app/models/people/sibling';
import type Person from 'linced-app/models/person';

// Proto: people.Learner.
export default class Learner extends Fragment {
  declare [Type]: 'people/learner';

  @service
  declare session: SessionService;

  @fragmentArray('people/contact')
  declare contacts: FragmentArray<Contact>;

  @fragmentArray('people/medical-contact')
  declare medicalContacts: FragmentArray<MedicalContact>;

  @filterBy('medicalContacts', 'isCurrent')
  declare currentMedicalContacts: unknown[];

  @computed('contacts.@each.{caregiverId,type,priority}')
  get primaryContacts() {
    return filterByArray(this.contacts, 'type', ContactType.PRIMARY).sort(
      byKeys('priority'),
    );
  }

  @computed('contacts.@each.{caregiverId,type,priority}')
  get secondaryContacts() {
    return filterByArray(this.contacts, 'type', ContactType.SECONDARY).sort(
      byKeys('priority'),
    );
  }

  @computed('contacts.@each.{caregiverId,type,priority}')
  get additionalContacts() {
    return filterByArray(this.contacts, 'type', ContactType.EMERGENCY).sort(
      byKeys('priority'),
    );
  }

  @attr('string', { defaultValue: '' })
  declare enrolmentId: string;

  /**
   * enrolment returns the learner's bound enrolment or null.
   * It performs a store peek record to return a concrete object instead of a
   * network calling promisable, so it expects that learner enrolments have been
   * preloaded into the store via the route handler before being called.
   *
   * @returns {DS.Model<linced-app/model/people/enrolment>}
   */
  @computed('enrolmentId')
  get enrolment() {
    const enrolmentId = this.enrolmentId;
    if (!isEmpty(enrolmentId)) {
      return this.store.peekRecord('people/enrolment', enrolmentId);
    }
    return null;
  }

  @fragmentArray('people/enrolmentStub')
  declare enrolments: FragmentArray<EnrolmentStub>;

  @computed(
    'enrolmentId',
    'enrolments.@each.{enrolmentId,schoolId}',
    'session.activeSchool',
  )
  get enrolmentStub() {
    // For transfer pre-enrolments, enrolmentId still points at the old school's
    // enrolment until PRE_ENROLLED→CURRENT fires.  Prefer the most recent stub
    // for the active school (stubs are prepended, so findBy returns the newest).
    const schoolId = this.get('session.activeSchool');
    if (schoolId) {
      const schoolStub = findBy(this.enrolments, 'schoolId', schoolId);
      if (schoolStub) {
        return schoolStub;
      }
    }
    return findBy(this.enrolments, 'enrolmentId', this.enrolmentId);
  }

  @attr()
  declare firstSchoolingDate: unknown;

  @attr('string', { defaultValue: '' })
  declare homeroomId: string;

  @attr('string', { defaultValue: '' })
  declare schoolCampusId: string;

  @attr('string', { defaultValue: '' })
  declare schoolId: string;

  // Sibling position is in ascending order of birthdate, i.e. descending order of age.
  @attr('number', { defaultValue: 1 })
  declare siblingPosition: number;

  @attr('number', { defaultValue: 1 })
  declare siblingCount: number;

  @fragmentArray('people/sibling')
  declare siblings: FragmentArray<Sibling>;

  // localSiblingIds gives the person ids of the siblings at the active
  // school. Sibling#person gives null for the other siblings, so a load of
  // the siblings uses only these ids.
  get localSiblingIds(): string[] {
    // TS-STANDIN(session): common session.js is JS, so activeSchool is any.
    const activeSchool: unknown = this.session.activeSchool;
    const ids: string[] = [];
    for (const sibling of this.siblings ?? []) {
      if (sibling.schoolId === activeSchool) {
        ids.push(sibling.personId);
      }
    }
    return ids;
  }

  // siblingPerson peeks the sibling's Person record directly from the
  // store. Unlike Sibling#person, it does not check the school and does not
  // load a person that is not in the store.
  siblingPerson(sibling: Sibling) {
    return this.store.peekRecord<Person>('person', sibling.personId);
  }

  // bySiblingPosition orders resolved siblings by their person's sibling
  // position.
  bySiblingPosition = (a: Sibling, b: Sibling) => {
    // WarpDrive types eachAttribute against keyof this, so this app's learner is not
    // assignable to the common Learner. Cast to this app's learner.
    return (
      ((this.siblingPerson(a)?.learner as Learner | undefined)
        ?.siblingPosition ?? 0) -
      ((this.siblingPerson(b)?.learner as Learner | undefined)
        ?.siblingPosition ?? 0)
    );
  };

  // Dependent keys stay on plain Sibling attrs (personId).
  @computed('siblings.@each.personId')
  get allSiblings() {
    const notArchived = this.siblings.filter((sibling) => {
      // WarpDrive types eachAttribute against keyof this, so this app's learner is not
      // assignable to the common Learner. Cast to this app's learner.
      const stub = (this.siblingPerson(sibling)?.learner as Learner | undefined)
        ?.enrolmentStub;
      return !isEmpty(stub) && stub!.status !== EnrolmentStatus.ARCHIVED;
    });
    return notArchived.sort(this.bySiblingPosition);
  }

  @computed('siblings.@each.personId')
  get currentSiblings() {
    const current = this.siblings.filter((sibling) => {
      // WarpDrive types eachAttribute against keyof this, so this app's learner is not
      // assignable to the common Learner. Cast to this app's learner.
      const stub = (this.siblingPerson(sibling)?.learner as Learner | undefined)
        ?.enrolmentStub;
      return (
        !isEmpty(stub) &&
        (stub!.status === EnrolmentStatus.CURRENT ||
          stub!.status === EnrolmentStatus.PENDING)
      );
    });
    return current.sort(this.bySiblingPosition);
  }

  @computed('siblings.@each.personId')
  get preEnrolledSiblings() {
    const preEnrolled = this.siblings.filter((sibling) => {
      // WarpDrive types eachAttribute against keyof this, so this app's learner is not
      // assignable to the common Learner. Cast to this app's learner.
      const stub = (this.siblingPerson(sibling)?.learner as Learner | undefined)
        ?.enrolmentStub;
      return !isEmpty(stub) && stub!.status === EnrolmentStatus.PRE_ENROLLED;
    });
    return preEnrolled.sort(this.bySiblingPosition);
  }

  @computed('siblings.@each.personId')
  get formerSiblings() {
    const former = this.siblings.filter((sibling) => {
      // WarpDrive types eachAttribute against keyof this, so this app's learner is not
      // assignable to the common Learner. Cast to this app's learner.
      const stub = (this.siblingPerson(sibling)?.learner as Learner | undefined)
        ?.enrolmentStub;
      return !isEmpty(stub) && stub!.status === EnrolmentStatus.FORMER;
    });
    return former.sort(this.bySiblingPosition);
  }

  @computed('siblingPosition')
  get isYoungest() {
    return this.siblingPosition === this.siblingCount;
  }

  @computed('siblingPosition', 'siblingCount')
  get isEldest() {
    return this.siblingPosition === 1;
  }

  @array('number')
  declare flags: StatefulArray<number>;

  @computed('contacts.@each.{caregiverId,billPayer,priority}')
  get billPayers() {
    return filterByArray(this.contacts, 'billPayer').sort(byKeys('priority'));
  }

  @fragmentArray('people/medical-condition')
  declare medicalConditions: FragmentArray<MedicalCondition>;

  @filterBy('medicalConditions', 'isCurrent')
  declare currentMedicalConditions: MedicalCondition[];

  @filterBy('medicalConditions', 'isCurrent', false)
  declare expiredMedicalConditions: unknown[];

  // This just returns the first medical condition iwth anaphy
  @computed('currentMedicalConditions.@each.anaphylaxis')
  get medicalConditionWithAnaphylaxis() {
    return this.currentMedicalConditions.find(
      (condition) => condition.anaphylaxis,
    );
  }
}
